import { useMemo } from 'react';
import type { WeatherHistoryEntryDto } from '../../api/weather.js';
import {
  formatTemperature,
  type TemperatureUnit,
} from '../../classroomPages/temperatureUnitStorage.js';
import sunnyImg from '../../../../../assets/weather/sunny.png';
import partlySunnyImg from '../../../../../assets/weather/partly-sunny.png';
import cloudyImg from '../../../../../assets/weather/cloudy.png';
import rainyImg from '../../../../../assets/weather/rainy.png';
import thunderImg from '../../../../../assets/weather/thunder.png';
import snowyImg from '../../../../../assets/weather/snowy.png';
import windyImg from '../../../../../assets/weather/windy.png';
import foggyImg from '../../../../../assets/weather/foggy.png';
import styles from './WeatherCharts.module.css';

export interface WeatherChartsProps {
  history: WeatherHistoryEntryDto[];
  unit: TemperatureUnit;
}

const KINDS = [
  { kind: 'sunny', label: 'Sunny', img: sunnyImg, color: '#f5b94c' },
  { kind: 'partly-sunny', label: 'Partly Sunny', img: partlySunnyImg, color: '#f3c674' },
  { kind: 'cloudy', label: 'Cloudy', img: cloudyImg, color: '#a8b3bd' },
  { kind: 'rainy', label: 'Rainy', img: rainyImg, color: '#5a8fd1' },
  { kind: 'thunder', label: 'Thunder', img: thunderImg, color: '#3a4a78' },
  { kind: 'snowy', label: 'Snowy', img: snowyImg, color: '#c7dde8' },
  { kind: 'windy', label: 'Windy', img: windyImg, color: '#cab98a' },
  { kind: 'foggy', label: 'Foggy', img: foggyImg, color: '#b8b094' },
] as const;

/** Temperature-bar color buckets — match the thermometer's gradient
 * stops so the charts feel connected to the live gauge. */
function tempColor(f: number): string {
  if (f < 40) return '#4a90ff'; // cold (blue)
  if (f < 65) return '#7fbfe8'; // cool (light blue / aqua)
  if (f < 85) return '#f5b94c'; // warm (amber — matches gradient mid)
  return '#e74c3c'; // hot (red)
}

export function WeatherCharts({ history, unit }: WeatherChartsProps) {
  // Index history by date for fast lookup, and figure out which month
  // we're charting (use the first entry; falls back to current month).
  const byDate = useMemo(() => {
    const m = new Map<string, WeatherHistoryEntryDto>();
    for (const row of history) m.set(row.date, row);
    return m;
  }, [history]);

  // Determine month + day count. If we have any history, use the
  // month of the first entry; otherwise the current month.
  const monthInfo = useMemo(() => {
    const sample = history[0]?.date;
    let year: number;
    let month: number; // 1-12
    if (sample && /^\d{4}-\d{2}-\d{2}$/.test(sample)) {
      const [y, m] = sample.split('-').map(Number) as [number, number];
      year = y;
      month = m;
    } else {
      const d = new Date();
      year = d.getFullYear();
      month = d.getMonth() + 1;
    }
    const daysInMonth = new Date(year, month, 0).getDate();
    return { year, month, daysInMonth };
  }, [history]);

  // Build the per-day temperature list (null for empty days). The
  // bar-chart's max temp determines bar-height scale; floor at 80 so
  // a single 50°F day doesn't render as a full-height bar.
  const dayTemps = useMemo(() => {
    const out: { day: number; temp: number | null; iso: string }[] = [];
    for (let d = 1; d <= monthInfo.daysInMonth; d++) {
      const iso = `${monthInfo.year}-${String(monthInfo.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const row = byDate.get(iso);
      out.push({ day: d, iso, temp: row?.temperature ?? null });
    }
    return out;
  }, [byDate, monthInfo]);

  const maxTemp = useMemo(() => {
    let m = 100; // sane floor so cool months still produce readable bars
    for (const d of dayTemps) {
      if (d.temp !== null && d.temp > m) m = d.temp;
    }
    return m;
  }, [dayTemps]);

  // Y-axis ticks for the temperature chart. Words-mode labels the four
  // descriptor bands (cold/cool/warm/hot) at the middle of each band.
  // F/C mode shows five evenly-spaced numeric ticks. `valueF` is the
  // underlying Fahrenheit used to position the label as a % from the
  // bottom of the chart.
  const yTicks = useMemo<{ valueF: number; label: string }[]>(() => {
    if (unit === 'words') {
      // Centre each band's label within its band.
      const bands: { valueF: number; label: string }[] = [
        { valueF: 20, label: 'Cold' },
        { valueF: 52, label: 'Cool' },
        { valueF: 75, label: 'Warm' },
        { valueF: Math.min(maxTemp - 4, 95), label: 'Hot' },
      ];
      return bands;
    }
    // Numeric: 5 ticks evenly stepping from 0 → maxTemp (rounded).
    const out: { valueF: number; label: string }[] = [];
    for (let i = 0; i <= 4; i++) {
      const f = Math.round((maxTemp / 4) * i);
      out.push({ valueF: f, label: formatTemperature(f, unit) });
    }
    return out;
  }, [unit, maxTemp]);

  // Per-kind day list: one entry per day that kind was picked, in
  // chronological order. Each entry becomes one rectangle in that
  // kind's column.
  const kindPicks = useMemo(() => {
    const map = new Map<string, WeatherHistoryEntryDto[]>();
    for (const k of KINDS) map.set(k.kind, []);
    for (const row of history) {
      if (row.kind && map.has(row.kind)) map.get(row.kind)!.push(row);
    }
    // Sort newest at the bottom of the stack so the bottom rectangle
    // is "today" — counts up easier for kids.
    for (const arr of map.values()) arr.sort((a, b) => a.date.localeCompare(b.date));
    return map;
  }, [history]);

  const hasAny = history.length > 0;

  return (
    <div className={styles.charts}>
      {/* ---------- Daily temperature bar chart ---------- */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>What was the temperature this month?</h3>
        </header>
        {!hasAny ? (
          <p className={styles.empty}>No weather logged yet this month.</p>
        ) : (
          <div className={styles.tempChart} data-testid="weather-temp-chart">
            <div className={styles.tempPlot}>
              {/* Y-axis: cold at the bottom, hot at the top. Labels
               *  are positioned absolutely by their proportional
               *  height so they line up with the bar values. */}
              <div className={styles.yAxis} aria-hidden="true">
                {yTicks.map(({ valueF, label }) => (
                  <span
                    key={`${valueF}-${label}`}
                    className={styles.yLabel}
                    style={{ bottom: `${Math.min(100, (valueF / maxTemp) * 100)}%` }}
                  >
                    {label}
                  </span>
                ))}
              </div>
              <div className={styles.tempBars}>
                {dayTemps.map(({ day, temp, iso }) => {
                  const heightPct = temp === null ? 0 : Math.max(4, (temp / maxTemp) * 100);
                  return (
                    <div key={iso} className={styles.tempCol}>
                      <div className={styles.tempBarWrap}>
                        {temp !== null ? (
                          <>
                            <span className={styles.tempValue}>
                              {/* Always show the numeric temperature above
                               *  the bar — the cold/cool/warm/hot
                               *  descriptors live on the y-axis at left,
                               *  so the top label can stay numeric for
                               *  every day. Words-mode classrooms still
                               *  see °F here. */}
                              {formatTemperature(temp, unit === 'words' ? 'F' : unit)}
                            </span>
                            <div
                              className={styles.tempBar}
                              style={{
                                height: `${heightPct}%`,
                                backgroundColor: tempColor(temp),
                              }}
                            />
                          </>
                        ) : null}
                      </div>
                      <span className={styles.tempDay}>{day}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ---------- Per-kind tally columns ---------- */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>What was the weather this month?</h3>
        </header>
        <div className={styles.kindChart} data-testid="weather-kind-chart">
          {KINDS.map((k) => {
            const picks = kindPicks.get(k.kind) ?? [];
            return (
              <div key={k.kind} className={styles.kindCol} data-kind={k.kind}>
                <div className={styles.kindStack}>
                  {picks.map((p) => (
                    <div
                      key={p.date}
                      className={styles.kindRect}
                      style={{ backgroundColor: k.color }}
                      title={p.date}
                    />
                  ))}
                </div>
                <div className={styles.kindCount}>{picks.length}</div>
                <img src={k.img} alt="" className={styles.kindIcon} />
                <div className={styles.kindLabel}>{k.label}</div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
